import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save } from 'lucide-react';
import { galleryService } from '@/services/galleryService';
import { locationService } from '@/services/locationService';
import { Button } from '@/components/ui/Button/Button';
import {
  CreateGalleryRequest,
  ProgramId,
  GalleryType,
  GalleryStatus,
  GalleryFormData
} from '@/types/gallery';
import { PROGRAM_OPTIONS, GALLERY_TYPE_OPTIONS } from '../../constants';

interface CreateGalleryProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export const CreateGallery: React.FC<CreateGalleryProps> = ({
  onSuccess,
  onCancel
}) => {
  const [formData, setFormData] = useState<GalleryFormData>({
    title: '',
    description: '',
    program_id: '' as ProgramId,
    type: 'village_gallery',
    location_id: '',
    status: 'Active'
  });
  const [errors, setErrors] = useState<Partial<GalleryFormData>>({});
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedSubdistrict, setSelectedSubdistrict] = useState('');

  // Fetch location data
  const { data: districts } = useQuery({
    queryKey: ['districts'],
    queryFn: () => locationService.getDistricts(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: subdistricts } = useQuery({
    queryKey: ['subdistricts', selectedDistrict],
    queryFn: () => locationService.getSubdistricts(selectedDistrict),
    enabled: !!selectedDistrict,
    staleTime: 5 * 60 * 1000,
  });

  const { data: villages } = useQuery({
    queryKey: ['villages', selectedDistrict, selectedSubdistrict],
    queryFn: () => locationService.getVillages(selectedDistrict, selectedSubdistrict),
    enabled: !!selectedDistrict && !!selectedSubdistrict,
    staleTime: 5 * 60 * 1000,
  });

  const queryClient = useQueryClient();

  // Create gallery mutation
  const createGalleryMutation = useMutation({
    mutationFn: (data: CreateGalleryRequest) => galleryService.createGallery(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['galleries'] });
      onSuccess();
    },
    onError: (error: Error) => {
      // Handle specific validation errors
      setErrors({ title: error.message });
    }
  });

  const handleInputChange = (field: keyof GalleryFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleDistrictChange = (districtName: string) => {
    setSelectedDistrict(districtName);
    setSelectedSubdistrict('');
    setFormData(prev => ({ ...prev, location_id: '' }));
  };

  const handleSubdistrictChange = (subdistrictName: string) => {
    setSelectedSubdistrict(subdistrictName);
    setFormData(prev => ({ ...prev, location_id: '' }));
  };

  const handleVillageChange = (villageId: string) => {
    setFormData(prev => ({ ...prev, location_id: villageId }));
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.program_id) {
      newErrors.program_id = 'Program is required';
    }

    if (!formData.location_id) {
      newErrors.location_id = 'Location is required';
    }

    setErrors(newErrors as Partial<GalleryFormData>);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    const createData: CreateGalleryRequest = {
      title: formData.title.trim(),
      description: formData.description?.trim() || undefined,
      program_id: formData.program_id,
      type: formData.type,
      location_id: formData.location_id,
      status: formData.status
    };

    createGalleryMutation.mutate(createData);
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="bg-white shadow-sm border border-rgf-gray-200 rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              icon={<ArrowLeft className="h-4 w-4" />}
            >
              Back
            </Button>
            <h2 className="ml-4 text-xl font-semibold text-rgf-gray-900">
              Create New Gallery
            </h2>
          </div>
        </div>
        <p className="mt-2 text-sm text-rgf-gray-600">
          Create a new visual gallery to showcase program impact by location
        </p>
      </div>

      {/* Form */}
      <div className="bg-white shadow-sm border border-rgf-gray-200 rounded-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-rgf-gray-900">Basic Information</h3>
            
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-rgf-gray-700 mb-1">
                Gallery Title *
              </label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter gallery title"
                className={`block w-full border rounded-lg px-3 py-2 text-sm focus:ring-rgf-primary-500 focus:border-rgf-primary-500 ${
                  errors.title ? 'border-red-300' : 'border-rgf-gray-300'
                }`}
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title}</p>
              )}
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-rgf-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter gallery description (optional)"
                rows={3}
                className="block w-full border border-rgf-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="program" className="block text-sm font-medium text-rgf-gray-700 mb-1">
                  Program *
                </label>
                <select
                  id="program"
                  value={formData.program_id}
                  onChange={(e) => handleInputChange('program_id', e.target.value as ProgramId)}
                  className="block w-full border border-rgf-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                >
                  <option value="">Select Program</option>
                  {PROGRAM_OPTIONS.map(program => (
                    <option key={program.value} value={program.value}>
                      {program.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="type" className="block text-sm font-medium text-rgf-gray-700 mb-1">
                  Gallery Type *
                </label>
                <select
                  id="type"
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value as GalleryType)}
                  className="block w-full border border-rgf-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                >
                  {GALLERY_TYPE_OPTIONS.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-rgf-gray-500">
                  {GALLERY_TYPE_OPTIONS.find(t => t.value === formData.type)?.description}
                </p>
              </div>
            </div>
          </div>

          {/* Location Selection */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-rgf-gray-900">Location</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="district" className="block text-sm font-medium text-rgf-gray-700 mb-1">
                  District *
                </label>
                <select
                  id="district"
                  value={selectedDistrict}
                  onChange={(e) => handleDistrictChange(e.target.value)}
                  className="block w-full border border-rgf-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                >
                  <option value="">Select District</option>
                  {districts?.map((district: any) => (
                    <option key={district.name || district} value={district.name || district}>
                      {district.name || district}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="subdistrict" className="block text-sm font-medium text-rgf-gray-700 mb-1">
                  Sub-district *
                </label>
                <select
                  id="subdistrict"
                  value={selectedSubdistrict}
                  onChange={(e) => handleSubdistrictChange(e.target.value)}
                  disabled={!selectedDistrict}
                  className="block w-full border border-rgf-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-rgf-primary-500 focus:border-rgf-primary-500 disabled:bg-rgf-gray-50 disabled:cursor-not-allowed"
                >
                  <option value="">Select Sub-district</option>
                  {subdistricts?.data?.list?.map((subdistrict: any) => (
                    <option key={subdistrict.subdistrict_name} value={subdistrict.subdistrict_name}>
                      {subdistrict.subdistrict_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="village" className="block text-sm font-medium text-rgf-gray-700 mb-1">
                  Village *
                </label>
                <select
                  id="village"
                  value={formData.location_id}
                  onChange={(e) => handleVillageChange(e.target.value)}
                  disabled={!selectedSubdistrict}
                  className={`block w-full border rounded-lg px-3 py-2 text-sm focus:ring-rgf-primary-500 focus:border-rgf-primary-500 disabled:bg-rgf-gray-50 disabled:cursor-not-allowed ${
                    errors.location_id ? 'border-red-300' : 'border-rgf-gray-300'
                  }`}
                >
                  <option value="">Select Village</option>
                  {villages?.data?.list?.map((village: any) => (
                    <option key={village.village_id} value={village.village_id}>
                      {village.village_name}
                    </option>
                  ))}
                </select>
                {errors.location_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.location_id}</p>
                )}
              </div>
            </div>
          </div>

          {/* Gallery Status */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-rgf-gray-700 mb-1">
              Status
            </label>
            <select
              id="status"
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value as GalleryStatus)}
              className="block w-full md:w-48 border border-rgf-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
            <p className="mt-1 text-xs text-rgf-gray-500">
              Active galleries are visible to end users
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-rgf-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={createGalleryMutation.isPending}
              icon={<Save className="h-4 w-4" />}
            >
              Create Gallery
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};